import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  StaggerCss,
  TitleSection,
  getNinePointLayoutProperties,
} from '@mentimeter/question-modules-shared';
import { QRCodeCanvas } from 'qrcode.react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export function InstructionsPresentation() {
  const {
    useQuestion,
    useTranslate,
    useVotingInstructions,
    useConfig,
    InteractiveCanvasComponents,
  } = usePresentationContext();

  const question = useQuestion();
  const title = question.question;
  const translate = useTranslate();
  const { baseUrl, votingUrl, voteKey, voteCode } = useVotingInstructions();
  const { enableAnimations } = useConfig();

  const { justifyContent, alignItems } = getNinePointLayoutProperties(
    question.layout,
  );

  const qrUrl = `${votingUrl}/${voteKey}?source=qr-instructions-slide`;

  const isEditor = Boolean(InteractiveCanvasComponents?.Title);

  return (
    <Box width="100%" height="100%" justifyContent="center">
      {(title || isEditor) && (
        <TitleSection
          title={question.question}
          styledTitle={question.question_styled}
          fontSizeOffset={question.layout?.font_size_offset}
          textAlign="center"
          placeholder="Instructions"
          enablePlaceholderAlignment
          mb="space4"
          showTextSizeDropdown={true}
        />
      )}

      {voteKey && isValidHttpUrl(qrUrl) && (
        <Box
          width="100%"
          flexDirection="row"
          height={title || isEditor ? '65%' : '100%'}
          zIndex={1}
          pt={4}
        >
          {voteCode && (
            <>
              <Box
                data-testid="vote-code-container"
                width="100%"
                height="100%"
                flex="1 1 auto"
                alignItems={alignItems}
                justifyContent={justifyContent}
              >
                <Text color="text" fontSize="225">
                  {translate('voting_instructions.go_to')}
                </Text>
                <Text
                  fontFamily="heading"
                  fontWeight="semiBold"
                  lineHeight="heading"
                  color="text"
                  fontSize="88px"
                >
                  {baseUrl}
                </Text>
                <Text mb={2} mt={4} fontSize="225" color="text">
                  {translate('voting_instructions.enter_code')}
                </Text>
                <StaggerCss
                  type="fade"
                  height="auto"
                  index={2}
                  enableAnimations={enableAnimations}
                >
                  <Text
                    fontFamily="heading"
                    fontWeight="semiBold"
                    lineHeight="heading"
                    color="text"
                    mb={0}
                    fontSize="88px"
                    data-testid="voting-code"
                  >
                    {voteCode}
                  </Text>
                </StaggerCss>
              </Box>
              <Box height="100%" width="1px" bg="border" />
            </>
          )}
          <Box
            width="100%"
            height="100%"
            flex="1 1 auto"
            alignItems={alignItems}
            justifyContent={justifyContent}
          >
            <Box height="100%" extend={() => ({ aspectRatio: '1' })}>
              <StaggerCss
                type="fade"
                index={2}
                enableAnimations={enableAnimations}
              >
                <Box
                  width="100%"
                  height="100%"
                  bg="white"
                  p={3}
                  borderRadius="25px"
                >
                  <QRCodeCanvas
                    value={qrUrl}
                    // Large size so it doesn't get blurry when upscale
                    size={1000}
                    // But display it at parent containers size
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '10px',
                    }}
                  />
                </Box>
              </StaggerCss>
            </Box>
            {voteCode && (
              <Text mt={3} fontSize="225" color="text">
                {translate('voting_instructions.or_qr')}
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
